import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, ReplaySubject, interval } from 'rxjs';
import { distinctUntilChanged, map, takeWhile } from 'rxjs/operators';

import { LoginAlertService } from '../authentication/login-alert.service';
import { ApiService } from './api.service';
import { AdminsApiService } from './admins-api.service';
import { JwtService } from './auth/jwt.service';
import { IUser } from '../api/types/IUser';
import { ITenant } from '../api/types/IUser';

class User implements IUser {
  id: '';
  email: '';
  name: '';
  token: '';
  role: '';
  tenant: ITenant;
}

@Injectable()
export class AdminService {
  private currentUserSubject = new BehaviorSubject<IUser>(new User());
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public currentUser = this.currentUserSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  private lastActive: number = Date.now();
  private renderer: Renderer2;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private jwtService: JwtService,
    private alertService: LoginAlertService,
    private adminsApiService: AdminsApiService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.updateToken();
  }

  updateLastActive() {
    this.lastActive = Date.now();
  }

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If JWT detected, attempt to get & store user's info

    // get the user info for super admin
    if (sessionStorage.getItem('role') === 'superadmin') {
      return this.apiService.get('/super').subscribe(
        (user) => this.setAuth(user),
        (err) => this.logout(true)
      );
    }

    // get the user info for admin.
    if (this.jwtService.getToken()) {
      return this.adminsApiService.getAdminAssociatedToToken().subscribe(
        (user) => this.setAuth(user),
        (err) => this.logout(true)
      );
    }

    // Remove any potential remnants of previous auth states
    this.logout(true);
  }

  setAuth(user: IUser) {
    // Set current user data into observable
    this.currentUserSubject.next(user);

    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);
  }

  logout(auto?: any) {
    if (!auto) {
      let logoutPath = '/logout';

      if (this.currentUserSubject.value.role === 'superadmin') {
        logoutPath = '/super/logout';
      }

      return this.apiService.get(logoutPath).pipe(
        map(() => {
          this.destroyToken();

          this.alertService.setAlert({
            type: 'error',
            message: 'You were signed out of your account.',
          });
          this.router.navigateByUrl('/login');
        })
      );
    }

    // Check if user was logged in before expiring the session
    if (this.jwtService.getToken()) {
      this.alertService.setAlert({
        type: 'error',
        message: 'Your session has expired. Please sign in again.',
      });
    }

    this.destroyToken();

    // Remove .modal-open class to restore page scroll
    this.renderer.removeClass(document.body, 'modal-open');
  }

  getCurrentUser(): IUser {
    return this.currentUserSubject.value;
  }

  login(credentials): Observable<IUser> {
    return this.apiService.post('/login', credentials).pipe(
      map((user) => {
        // Save JWT sent from server in localstorage
        this.jwtService.saveToken(user.token);

        this.setAuth(user);

        return user;
      })
    );
  }

  private destroyToken() {
    // Remove JWT from localstorage
    this.jwtService.destroyToken();

    // remove role from the session storage
    sessionStorage.removeItem('role');

    // Set current user to an empty object
    this.currentUserSubject.next(new User());

    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
  }

  private updateToken() {
    interval(20 * 60 * 1000)
      .pipe(takeWhile(() => !false))
      .subscribe((i) => {
        if (
          Date.now() - this.lastActive < 15 * 60 * 1000 &&
          this.jwtService.getToken() &&
          sessionStorage.getItem('role') !== 'superadmin'
        ) {
          this.refreshLogin();
        }
      });
  }

  private refreshLogin() {
    this.apiService.get('/login').subscribe(
      (user) => {
        // Save JWT sent from server in localstorage
        this.jwtService.saveToken(user.token);
      },
      (err) => {
        this.logout(true);

        this.router.navigateByUrl('/login');
      }
    );
  }
}
